// src/components/Modals/ExecuteWorkflowModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Paper,
  IconButton,
  Switch,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import JSONView from '@uiw/react-json-view';
import WorkflowService from '../../services/WorkflowService';
import { useEnvironment } from '../../context/EnvironmentContext';
import HtmlPreviewModal from './HtmlPreviewModal';
import { useApiToken } from '../../hooks/useApiToken';

interface ExecuteWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  workflow: any;
  useJsonEndpoint?: boolean;
  isAgentExecution?: boolean;
  onFormSubmit?: (formData: Record<string, any>) => void;
}

const ExecuteWorkflowModal: React.FC<ExecuteWorkflowModalProps> = ({
  open,
  onClose,
  workflow,
  useJsonEndpoint = false,
  isAgentExecution = false,
  onFormSubmit
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const { environment } = useEnvironment();
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [jsonInput, setJsonInput] = useState('');
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (workflow?.input) {
      setInputs(workflow.input);
      setJsonInput(JSON.stringify(workflow.input, null, 2));
    }
  }, [workflow]);

  const handleInputChange = (key: string, value: any) => {
    setInputs(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddInput = () => {
    if (!newKey.trim()) {
      enqueueSnackbar('Please enter a key name', { variant: 'error' });
      return;
    }
    setInputs(prev => ({
      ...prev,
      [newKey]: newValue
    }));
    setNewKey('');
    setNewValue('');
  };

  const handleRemoveInput = (key: string) => {
    setInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[key];
      return newInputs;
    });
  };

  const handleJsonInputChange = (value: string) => {
    setJsonInput(value);
    setJsonError('');
    try {
      if (value.trim()) {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          setInputs(parsed);
        } else {
          setJsonError('JSON must be an object');
        }
      } else {
        setInputs({});
      }
    } catch (error) {
      setJsonError('Invalid JSON format');
    }
  };
  const handleModeToggle = (checked: boolean) => {
    setIsJsonMode(checked);
    if (checked) {
      setJsonInput(JSON.stringify(inputs, null, 2));
    } else {
      if (jsonInput.trim() && !jsonError) {
        try {
          const parsed = JSON.parse(jsonInput);
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            setInputs(parsed);
          }
        } catch (error) {
          // Keep current inputs if JSON is invalid
        }
      }
    }
  };

  const handleHtmlFormSubmit = (formData: Record<string, any>) => {
    // Add form data to current inputs
    setInputs(prev => ({
      ...prev,
      ...formData
    }));

    // Also call the parent's onFormSubmit if provided
    if (onFormSubmit) {
      onFormSubmit(formData);
    }

    enqueueSnackbar('Form data added to workflow inputs', { variant: 'success' });
    setShowHtmlPreview(false);
  };

  const getAccessToken = useApiToken();

  const handleExecute = async () => {
    if (!workflow) {
      enqueueSnackbar('No workflow selected for execution', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      let response;

      const mergedInputs = {
        ...workflow.input,
        ...inputs
      };

      if (isAgentExecution) {
        // Execute agent
        if (!workflow.id) {
          throw new Error('Agent ID is required');
        }
        response = await workflowService.executeAgent(workflow.id, mergedInputs);
      } else if (useJsonEndpoint) {
        const workflowWithInputs = {
          ...workflow,
          input: mergedInputs
        };
        response = await workflowService.executeWorkflowJson(workflowWithInputs);
      } else {
        if (!workflow.id) {
          throw new Error('Workflow ID is required');
        }
        response = await workflowService.executeWorkflow(workflow.id, mergedInputs);
      }

      // Check if output is HTML
      if (response.data?.workflow_final_output) {
        const output = response.data.workflow_final_output;
        
        // Ensure output is a string before calling string methods
        const outputString = typeof output === 'string' ? output : String(output);
        
        // Check if it's a code block containing HTML
        if (outputString.startsWith('```html') && outputString.endsWith('```')) {
          setHtmlContent(outputString.replace(/^```html\n/, '').replace(/\n```$/, ''));
          setShowHtmlPreview(true);
        }
        // Check if it's direct HTML
        else if (outputString.trim().startsWith('<') && outputString.trim().endsWith('>')) {
          setHtmlContent(outputString);
          setShowHtmlPreview(true);
        }
      }

      setResult(response);
      enqueueSnackbar('Workflow executed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to execute workflow', { variant: 'error' });
      console.error('Execution error:', error);
      setResult({ error: error.message || 'Failed to execute workflow' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isAgentExecution ? "Execute Agent" : "Execute Workflow"}
        </DialogTitle>
        <DialogContent>
          {!workflow ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
          <Box sx={{ mt: 2 }}>
            <div>
              {!useJsonEndpoint && !isAgentExecution && workflow && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>Workflow ID</Typography>
                  <TextField
                    value={workflow.id || ''}
                    disabled
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {isAgentExecution && workflow && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>Agent ID</Typography>
                  <TextField
                    value={workflow.id || ''}
                    disabled
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Input Parameters</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color={isJsonMode ? 'text.secondary' : 'primary.main'}>Form</Typography>
                  <Switch
                    checked={isJsonMode}
                    onChange={(_, checked) => handleModeToggle(checked)}
                    size="small"
                  />
                  <Typography variant="body2" color={isJsonMode ? 'primary.main' : 'text.secondary'}>JSON</Typography>
                </Box>
              </Box>
              {isJsonMode ? (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    multiline
                    minRows={8}
                    maxRows={16}
                    value={jsonInput}
                    onChange={e => handleJsonInputChange(e.target.value)}
                    placeholder='{"key1": "value1", "key2": "value2"}'
                    fullWidth
                    sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}
                  />
                  {jsonError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{jsonError}</Alert>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Enter a JSON object with key-value pairs for workflow inputs
                  </Typography>
                </Box>
              ) : (
                <>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <TextField
                    placeholder="Key"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    size="small"
                  />
                  <TextField
                    placeholder="Value"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddInput}
                    size="small"
                  >
                    Add
                  </Button>
                </Stack>
              </Box>
              {Object.entries(inputs).map(([key, value]) => (
                <Box key={`input-${key}`} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label={key}
                    value={value}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveInput(key)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
                </>
              )}
            </div>

            {result && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Execution Result</Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <JSONView value={result} />
                  )}
                </Paper>
              </Box>
            )}
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleExecute}
            disabled={loading || !workflow}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Execute
          </Button>
        </DialogActions>
      </Dialog>

      <HtmlPreviewModal
        open={showHtmlPreview}
        onClose={() => setShowHtmlPreview(false)}
        html={htmlContent}
        onFormSubmit={handleHtmlFormSubmit}
      />
    </>
  );
};

export default ExecuteWorkflowModal;